import React, { useCallback, useState } from "react";

import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";

import { useDebounced } from "utils/hooks";

/**
 * QueryStringSearch allows filtering that plays nicely with navigation by
 * persisting filter state to the query string. Filter state is updated with
 * `replace: true` to avoid adding history entries.
 *
 * Updates to the query string are debounced by default.
 */
function QueryStringSearch({
  param,
  name,
  className,
  debounceMs = 40,
  placeholder,
}: {
  /** parameter to use in the query string (must be unique for any QueryStringSearch appearing on the same route) */
  param: string;
  /** name attribute to use for input element */
  name?: string;
  /** additional styling */
  className?: string;
  /** debounce interval, in milliseconds (default 40) */
  debounceMs?: number;
  /** placeholder to use (default "Search...") */
  placeholder?: string;
}) {
  const [value, setValue] = useState(
    new URLSearchParams(window.location.search).get(param) ?? "",
  );
  const navigate = useNavigate();
  const updateRaw = useCallback(
    (value: string) => {
      const newSearch = new URLSearchParams(window.location.search);
      if (value) {
        newSearch.set(param, value);
      } else {
        newSearch.delete(param);
      }
      const hash = window.location.hash;
      navigate(
        {
          search: newSearch.toString(),
          hash,
        },
        {
          replace: true,
        },
      );
    },
    [param, navigate],
  );
  const updateURLSearch = useDebounced(updateRaw, debounceMs);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setValue(value);
    updateURLSearch(value);
  };
  return (
    <input
      placeholder={placeholder ?? "Search..."}
      type="text"
      name={name}
      value={value}
      className={classNames(
        "bg-white rounded-[4px] border border-solid border-[#ccc] box-content text-[#555] text-sm h-5 leading-5 py-1 px-1.5 w-[206px]",
        className,
      )}
      onChange={handleInputChange}
    />
  );
}

export function useQueryStringSearchTerm(param: string) {
  const loc = useLocation();
  const value = new URLSearchParams(loc.search).get(param);

  return value ?? "";
}

export function useQueryStringSearch(param: string) {
  const term = useQueryStringSearchTerm(param);
  const SearchInput = useCallback(
    (props: Omit<React.ComponentProps<typeof QueryStringSearch>, "param">) => {
      return <QueryStringSearch param={param} {...props} />;
    },
    [param],
  );
  return [term, SearchInput] as const;
}

export default QueryStringSearch;
